/**
 * Custom React hook for AI System execution via WebSocket.
 * 
 * Replaces HTTP polling with real-time WebSocket streaming for:
 * - Starting AI System executions
 * - Receiving expert progress updates
 * - Getting execution completion notifications
 * 
 * Benefits over polling:
 * - ~90% reduction in HTTP requests
 * - Instant updates (vs 1s polling delay)
 * - Lower bandwidth usage
 * - Better scalability
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/auth';
import { executionApi } from '../services/api';
import type {
  AISystemExecution,
} from '../types';

// Execution status type
type AISystemExecutionStatus = 'PENDING' | 'START' | 'RUNNING' | 'SUCCESS' | 'COMPLETED' | 'FAILED' | 'ERROR' | 'CANCELLED' | 'CANCELED' | 'STOPPED' | 'TIMEOUT';

// WebSocket message types for AI System execution
export interface AISystemWSMessage {
  type: 
    | 'connected'
    | 'execution_started'
    | 'expert_started'
    | 'expert_completed'
    | 'expert_skipped'
    | 'execution_progress'
    | 'execution_completed'
    | 'execution_error'
    | 'execution_status'
    | 'stop_requested'
    | 'subscribed'
    | 'unsubscribed'
    | 'pong'
    | 'error';
  system_id?: number;
  system_name?: string;
  execution_id?: string;
  expert_id?: number;
  expert_name?: string;
  expert_type?: string;
  status?: string;
  result?: Record<string, any>;
  total_cost?: number;
  iteration?: number | null;
  progress?: number;
  experts_completed?: number;
  experts_total?: number;
  error?: string;
  message?: string;
  reason?: string;
  action?: string;
  // For execution_status
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
  expert_results?: Array<{
    expert_id: number;
    expert_name: string;
    expert_type: string;
    status: string;
    total_cost: number;
    iteration?: number | null;
  }>;
  metadata?: Record<string, any>;
  logs?: string;
}

export interface ExpertProgress {
  expert_id: number;
  expert_name: string;
  expert_type: string;
  status: 'pending' | 'running' | 'completed' | 'skipped' | 'stopped' | 'error';
  total_cost?: number;
  result?: Record<string, any>;
  iteration?: number | null;
}

export interface UseAISystemSocketOptions {
  /** AI System ID to connect to */
  systemId: number;
  /** Called when connection is established */
  onConnect?: () => void;
  /** Called when connection is closed */
  onDisconnect?: () => void;
  /** Called when any message is received */
  onMessage?: (message: AISystemWSMessage) => void;
  /** Called when execution starts */
  onExecutionStarted?: (executionId: string) => void;
  /** Called when an expert starts */
  onExpertStarted?: (expertId: number, expertName: string) => void;
  /** Called when an expert completes */
  onExpertCompleted?: (expertId: number, expertName: string, status: string, result?: any) => void;
  /** Called with progress updates */
  onProgress?: (progress: number, expertsCompleted: number, expertsTotal: number) => void;
  /** Called when execution completes successfully */
  onExecutionCompleted?: (executionId: string, status: string, result?: any, totalCost?: number) => void;
  /** Called when execution fails */
  onExecutionError?: (executionId: string, error: string) => void;
  /** Called when execution stop is acknowledged */
  onExecutionStopped?: (executionId: string) => void;
  /** Auto-connect on mount (default: true) */
  autoConnect?: boolean;
}

export interface UseAISystemSocketReturn {
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** Whether an execution is currently running */
  isExecuting: boolean;
  /** Current execution ID (if any) */
  currentExecutionId: string | null;
  /** Current execution status */
  executionStatus: string | null;
  /** Progress percentage (0-100) */
  progress: number;
  /** Expert progress tracking */
  expertProgress: ExpertProgress[];
  /** Last error message */
  error: string | null;
  /** Total cost of current execution */
  totalCost: number;
  /** Start a new execution */
  startExecution: (metadata?: Record<string, any>, executionType?: string) => void;
  /** Stop the current execution */
  stopExecution: () => void;
  /** Subscribe to an existing execution */
  subscribeToExecution: (executionId: string) => void;
  /** Get current status of an execution */
  getExecutionStatus: (executionId: string) => void;
  /** Manually connect to WebSocket */
  connect: () => void;
  /** Manually disconnect from WebSocket */
  disconnect: () => void;
}

const normalizeExpertStatus = (status?: string): ExpertProgress['status'] => {
  const normalized = (status || '').toLowerCase();

  if (['success', 'complete', 'completed', 'cached'].includes(normalized)) {
    return 'completed';
  }

  if (normalized === 'skipped') {
    return 'skipped';
  }

  if (['running', 'start', 'started', 'pending'].includes(normalized)) {
    return 'running';
  }

  if (['stopped', 'cancelled', 'canceled'].includes(normalized)) {
    return 'stopped';
  }

  if (['error', 'failed', 'failure', 'timeout'].includes(normalized)) {
    return 'error';
  }

  return 'completed';
};

const getExpertProgressKey = (expertId?: number, iteration?: number | null): string => {
  return `${expertId ?? 'unknown'}:${iteration ?? 'main'}`;
};

const TERMINAL_AI_SYSTEM_STATUSES = new Set([
  'SUCCESS',
  'COMPLETED',
  'FAILED',
  'ERROR',
  'CANCELLED',
  'CANCELED',
  'STOPPED',
  'TIMEOUT',
]);

/**
 * Hook for managing AI System execution via WebSocket
 */
export function useAISystemSocket(options: UseAISystemSocketOptions): UseAISystemSocketReturn {
  const {
    systemId,
    onConnect,
    onDisconnect,
    onMessage,
    onExecutionStarted,
    onExpertStarted,
    onExpertCompleted,
    onProgress,
    onExecutionCompleted,
    onExecutionError,
    onExecutionStopped,
    autoConnect = true,
  } = options;

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  const [executionStatus, setExecutionStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [expertProgress, setExpertProgress] = useState<ExpertProgress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [totalCost, setTotalCost] = useState(0);

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef(false); // Prevent concurrent connection attempts
  const reconnectAttemptsRef = useRef(0);
  const shouldRecoverOnOpenRef = useRef(false);
  const currentExecutionIdRef = useRef<string | null>(null);
  const startedExecutionIdsRef = useRef<Set<string>>(new Set());
  const stopRequestedExecutionIdsRef = useRef<Set<string>>(new Set());
  const maxReconnectAttempts = 5;

  // Build WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const token = authService.getAccessToken();
    if (!token) {
      console.error('No auth token available for WebSocket connection');
      return null;
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.hostname;
    const isProduction = window.location.protocol === 'https:';
    const wsPort = isProduction ? '' : ':8000';
    
    return `${wsProtocol}//${wsHost}${wsPort}/ws/ai-systems/${systemId}/execution/?token=${token}`;
  }, [systemId]);

  useEffect(() => {
    currentExecutionIdRef.current = currentExecutionId;
  }, [currentExecutionId]);

  const recoverExecutionFromApi = useCallback(async (executionId: string, ws?: WebSocket | null) => {
    try {
      const execution = await executionApi.getSystemDetail(executionId);
      const status = String(execution.status || '').toUpperCase();
      const expertResults = Array.isArray(execution.expert_results)
        ? execution.expert_results
        : [];
      const expertsCompleted = expertResults.filter((expert) => (
        TERMINAL_AI_SYSTEM_STATUSES.has(String(expert.status || '').toUpperCase()) ||
        ['success', 'completed', 'cached', 'skipped'].includes(String(expert.status || '').toLowerCase())
      )).length;
      const expertsTotal = expertResults.length;

      setCurrentExecutionId(executionId);
      setExecutionStatus(status || null);
      setTotalCost(Number(execution.total_cost || 0));
      if (expertsTotal > 0) {
        setExpertProgress(expertResults.map((expert) => ({
          expert_id: expert.expert_id,
          expert_name: expert.expert_name,
          expert_type: expert.expert_type,
          status: normalizeExpertStatus(expert.status),
          total_cost: expert.total_cost,
          result: expert.parsed_output,
          iteration: expert.iteration ?? null,
        })));
        setProgress(TERMINAL_AI_SYSTEM_STATUSES.has(status)
          ? 100
          : Math.round((expertsCompleted / expertsTotal) * 100));
        onProgress?.(
          TERMINAL_AI_SYSTEM_STATUSES.has(status) ? 100 : Math.round((expertsCompleted / expertsTotal) * 100),
          expertsCompleted,
          expertsTotal,
        );
      }

      if (TERMINAL_AI_SYSTEM_STATUSES.has(status)) {
        setIsExecuting(false);
        currentExecutionIdRef.current = null;
        setCurrentExecutionId(null);
        if (['SUCCESS', 'COMPLETED'].includes(status)) {
          onExecutionCompleted?.(executionId, status, execution.result, Number(execution.total_cost || 0));
        } else if (['CANCELLED', 'CANCELED', 'STOPPED'].includes(status)) {
          onExecutionStopped?.(executionId);
        } else {
          setError(execution.logs || execution.result || status);
          onExecutionError?.(executionId, execution.logs || execution.result || status);
        }
        return;
      }

      setIsExecuting(true);
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'subscribe', execution_id: executionId }));
        ws.send(JSON.stringify({ type: 'get_status', execution_id: executionId }));
      }
    } catch (err) {
      console.warn('[AISystemSocket] Failed to recover execution after reconnect:', err);
    }
  }, [onExecutionCompleted, onExecutionError, onExecutionStopped, onProgress]);

  // Handle incoming messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data: AISystemWSMessage = JSON.parse(event.data);
      console.log('[AISystemSocket] Message received:', data.type, data);

      // Call generic message handler
      onMessage?.(data);

      switch (data.type) {
        case 'connected':
          console.log(`Connected to AI System ${data.system_name} (ID: ${data.system_id})`);
          break;

        case 'execution_started': {
          const executionId = data.execution_id || null;
          const alreadyStarted = executionId
            ? startedExecutionIdsRef.current.has(executionId)
            : false;

          setCurrentExecutionId(executionId);
          if (executionId) {
            startedExecutionIdsRef.current.add(executionId);
            stopRequestedExecutionIdsRef.current.delete(executionId);
          }
          setIsExecuting(true);
          setExecutionStatus(data.status || 'START');

          if (!alreadyStarted) {
            setProgress(0);
            setExpertProgress([]);
            setError(null);
            setTotalCost(0);
            if (executionId) {
              onExecutionStarted?.(executionId);
            }
          }
          break;
        }

        case 'expert_started':
          setExpertProgress(prev => {
            const iteration = data.iteration ?? null;
            const key = getExpertProgressKey(data.expert_id, iteration);
            const baseKey = getExpertProgressKey(data.expert_id, null);
            const scopedPrev = iteration == null
              ? prev
              : prev.filter(e => getExpertProgressKey(e.expert_id, e.iteration) !== baseKey);
            const exists = scopedPrev.some(e => getExpertProgressKey(e.expert_id, e.iteration) === key);
            if (exists) {
              return scopedPrev.map(e =>
                getExpertProgressKey(e.expert_id, e.iteration) === key
                  ? { ...e, status: 'running' as const, iteration }
                  : e
              );
            }
            return [...scopedPrev, {
              expert_id: data.expert_id!,
              expert_name: data.expert_name!,
              expert_type: data.expert_type!,
              status: 'running' as const,
              iteration,
            }];
          });
          onExpertStarted?.(data.expert_id!, data.expert_name!);
          break;

        case 'expert_completed':
          setExpertProgress(prev => {
            const nextStatus = normalizeExpertStatus(data.status);
            const iteration = data.iteration ?? null;
            const key = getExpertProgressKey(data.expert_id, iteration);
            const baseKey = getExpertProgressKey(data.expert_id, null);
            const scopedPrev = iteration == null
              ? prev
              : prev.filter(e => getExpertProgressKey(e.expert_id, e.iteration) !== baseKey);
            const exists = scopedPrev.some(e => getExpertProgressKey(e.expert_id, e.iteration) === key);

            if (!exists) {
              return [...scopedPrev, {
                expert_id: data.expert_id!,
                expert_name: data.expert_name!,
                expert_type: data.expert_type || '',
                status: nextStatus,
                total_cost: data.total_cost,
                result: data.result,
                iteration,
              }];
            }

            return scopedPrev.map(e =>
              getExpertProgressKey(e.expert_id, e.iteration) === key
                ? {
                    ...e,
                    status: nextStatus,
                    total_cost: data.total_cost,
                    result: data.result,
                    iteration,
                  }
                : e
            );
          });
          onExpertCompleted?.(data.expert_id!, data.expert_name!, data.status!, data.result);
          break;

        case 'expert_skipped':
          setExpertProgress(prev => {
            const exists = prev.some(e => e.expert_id === data.expert_id);
            if (exists) {
              return prev.map(e =>
                e.expert_id === data.expert_id
                  ? { ...e, status: 'skipped' as const }
                  : e
              );
            }
            return [...prev, {
              expert_id: data.expert_id!,
              expert_name: data.expert_name!,
              expert_type: data.expert_type || '',
              status: 'skipped' as const,
            }];
          });
          break;

        case 'execution_progress':
          setProgress(data.progress || 0);
          onProgress?.(data.progress || 0, data.experts_completed || 0, data.experts_total || 0);
          break;

        case 'execution_completed':
          setIsExecuting(false);
          setExecutionStatus(data.status || 'COMPLETED');
          setProgress(100);
          setTotalCost(data.total_cost || 0);
          onExecutionCompleted?.(data.execution_id!, data.status!, data.result, data.total_cost);
          break;

        case 'execution_error': {
          const errorExecutionId = data.execution_id || currentExecutionId || '';
          if (
            stopRequestedExecutionIdsRef.current.has(errorExecutionId) ||
            (executionStatus || '').toLowerCase() === 'stopped' ||
            /execution stopped by user request|stop requested/i.test(data.error || '')
          ) {
            setIsExecuting(false);
            setExecutionStatus('STOPPED');
            setError(null);
            break;
          }

          setIsExecuting(false);
          setExecutionStatus('ERROR');
          setError(data.error || 'Unknown error');
          onExecutionError?.(data.execution_id || currentExecutionId || '', data.error || 'Unknown error');
          break;
        }

        case 'execution_status':
          // Full status update (from subscribe or get_status)
          setExecutionStatus(data.status || null);
          if ((data.status || '').toLowerCase() === 'running') {
            setIsExecuting(true);
          } else if (['completed', 'error', 'stopped'].includes((data.status || '').toLowerCase())) {
            setIsExecuting(false);
          }
          if (data.expert_results) {
            setExpertProgress(data.expert_results.map(r => ({
              expert_id: r.expert_id,
              expert_name: r.expert_name,
              expert_type: r.expert_type,
              status: normalizeExpertStatus(r.status),
              total_cost: r.total_cost,
              iteration: r.iteration ?? null,
            })));
          }
          break;

        case 'stop_requested':
          console.log('Stop requested:', data.message);
          if (data.execution_id || currentExecutionId) {
            stopRequestedExecutionIdsRef.current.add(data.execution_id || currentExecutionId || '');
          }
          setIsExecuting(false);
          setExecutionStatus('STOPPED');
          setError(null);
          setExpertProgress(prev =>
            prev.map((expert) =>
              expert.status === 'running' || expert.status === 'pending'
                ? { ...expert, status: 'stopped' as const }
                : expert
            )
          );
          onExecutionStopped?.(data.execution_id || currentExecutionId || '');
          break;

        case 'error':
          setError(data.error || 'Unknown error');
          console.error('[AISystemSocket] Error:', data.error);
          break;

        case 'pong':
          // Heartbeat response - connection is alive
          break;

        default:
          console.log('[AISystemSocket] Unknown message type:', data.type);
      }
    } catch (err) {
      console.error('[AISystemSocket] Failed to parse message:', err);
    }
  }, [currentExecutionId, executionStatus, onMessage, onExecutionStarted, onExpertStarted, onExpertCompleted, onProgress, onExecutionCompleted, onExecutionError, onExecutionStopped]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Don't connect if already connected, connecting, or a connection attempt is in progress
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING ||
      isConnectingRef.current
    ) {
      console.log('[AISystemSocket] Skipping connection - already connected or connecting');
      return;
    }

    // Check max reconnect attempts
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('[AISystemSocket] Max reconnect attempts reached, stopping');
      setError('Max reconnection attempts reached');
      return;
    }

    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      setError('Authentication required');
      return;
    }

    isConnectingRef.current = true;
    console.log('[AISystemSocket] Connecting to:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[AISystemSocket] Connected');
      isConnectingRef.current = false;
      const shouldRecover = shouldRecoverOnOpenRef.current || reconnectAttemptsRef.current > 0;
      shouldRecoverOnOpenRef.current = false;
      reconnectAttemptsRef.current = 0; // Reset on successful connection
      setIsConnected(true);
      setError(null);
      onConnect?.();

      // Start ping interval to keep connection alive
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000); // Ping every 30 seconds

      if (shouldRecover && currentExecutionIdRef.current) {
        void recoverExecutionFromApi(currentExecutionIdRef.current, ws);
      }
    };

    ws.onmessage = handleMessage;

    ws.onerror = (event) => {
      console.error('[AISystemSocket] Error:', event);
      isConnectingRef.current = false;
      setError('WebSocket connection error');
    };

    ws.onclose = (event) => {
      console.log('[AISystemSocket] Disconnected:', event.code, event.reason);
      isConnectingRef.current = false;
      setIsConnected(false);
      wsRef.current = null;
      onDisconnect?.();

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Auto-reconnect with exponential backoff (unless intentionally closed)
      if (event.code === 4000) {
        // Server redeploying — reconnect immediately, skip backoff
        console.log('[AISystemSocket] Server redeploying, reconnecting immediately...');
        shouldRecoverOnOpenRef.current = true;
        reconnectAttemptsRef.current = 0;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 100);
      } else if (event.code !== 1000 && event.code !== 4001) {
        shouldRecoverOnOpenRef.current = true;
        reconnectAttemptsRef.current += 1;
        const backoffDelay = Math.min(3000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);
        console.log(`[AISystemSocket] Reconnecting in ${backoffDelay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, backoffDelay);
      }
    };
  }, [getWebSocketUrl, handleMessage, onConnect, onDisconnect, recoverExecutionFromApi]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    // Reset connection tracking
    isConnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    shouldRecoverOnOpenRef.current = false;

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // Start execution
  const startExecution = useCallback((metadata?: Record<string, any>, executionType?: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('WebSocket not connected');
      return;
    }

    // Reset state
    setError(null);
    setProgress(0);
    setExpertProgress([]);
    setTotalCost(0);

    wsRef.current.send(JSON.stringify({
      type: 'execute',
      execution_type: executionType || 'MANUAL',
      metadata: metadata || {},
    }));
  }, []);

  // Stop execution
  const stopExecution = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !currentExecutionId) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'stop',
      execution_id: currentExecutionId,
    }));
  }, [currentExecutionId]);

  // Subscribe to existing execution
  const subscribeToExecution = useCallback((executionId: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('WebSocket not connected');
      return;
    }

    setCurrentExecutionId(executionId);
    wsRef.current.send(JSON.stringify({
      type: 'subscribe',
      execution_id: executionId,
    }));
  }, []);

  // Get execution status
  const getExecutionStatus = useCallback((executionId: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'get_status',
      execution_id: executionId,
    }));
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      // Debounce connection to avoid rapid reconnection attempts
      const connectionTimeout = setTimeout(() => {
        connect();
      }, 100);

      return () => {
        clearTimeout(connectionTimeout);
        disconnect();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect, systemId]); // Only depend on autoConnect and systemId, not connect/disconnect

  return {
    isConnected,
    isExecuting,
    currentExecutionId,
    executionStatus,
    progress,
    expertProgress,
    error,
    totalCost,
    startExecution,
    stopExecution,
    subscribeToExecution,
    getExecutionStatus,
    connect,
    disconnect,
  };
}

export default useAISystemSocket;
